/**
 * Feature E02-F01 — React Dashboard: stat cards, projects table, filter-tabs,
 * scope pills + facet filters (+ T06 "New Project" button host).
 *
 * Ports `../EER app/public/assets/js/dashboard.js → renderProjectList`, extended
 * by the T08/T09/T10 filter work:
 *   - three stat cards (Awaiting Kick-off / In Progress / Launched), counted
 *     CLIENT-SIDE off stage/status (computeStats); clicking a card TOGGLES its
 *     filter (toggleFilter) and the active card is ring-highlighted;
 *   - scope pills "All" / "My Active" (T08 — matchesScope, `owner_id === currentUser.id
 *     && status === 'active'`);
 *   - a created_at date range (T09 — matchesCreatedRange) and dedicated Client +
 *     Position autocomplete facets (T10 — matchesClient / matchesPosition) that
 *     replaced the old single merged search box;
 *   - the table: Client/Position, Stage (badge), Owner (avatar initials), Updated;
 *     a row click OPENS the project (`onOpen(id)` → router navigate /project/:id);
 *   - empty states: an empty portfolio → "No projects"; a filter with no hits →
 *     "No projects match your search".
 *
 * Thin shell — every rule (stats, filter toggle, scope/date/facet predicates,
 * badge/owner projection, create-reply reading) lives in `dashboardModel.ts` so it
 * is unit-tested DOM-free; portfolio data comes from `useProjects()` (GET /projects),
 * the Client facet cache from `useClientsCache()` (GET /clients), and the create flow
 * from `NewProjectButton` (useCreateManualProject).
 */
import { useMemo, useState, type JSX } from 'react';
import type { ClientOption } from '@eer/shared';
import { useProjects } from '../project/useProjects.js';
import { useClientsCache } from '../project/projectDetailApi.js';
import { ClientAutocomplete } from '../project/ClientAutocomplete.js';
import { NewProjectButton } from './NewProjectButton.js';
import {
  applyDashboardFilters,
  clientLabel,
  computeStats,
  distinctRoleNames,
  formatUpdated,
  ownerInitials,
  ownerLabel,
  roleLabel,
  stageBadge,
  toggleFilter,
  type DashboardFilter,
  type DashboardScope,
} from './dashboardModel.js';

export interface DashboardViewProps {
  /** Open a project's detail card (router navigate to `/project/:id`). */
  readonly onOpen: (projectId: number) => void;
  /**
   * The authenticated principal's id (`AuthIdentity.id`), threaded from the shell.
   * Drives the "My Active" scope pill (E02-F01-T08): `owner_id === currentUserId`.
   * Null only defensively (the authed shell always supplies a real id).
   */
  readonly currentUserId?: string | null;
}

/** One stat card descriptor. */
interface StatCardDef {
  readonly filter: DashboardFilter;
  readonly label: string;
  readonly count: number;
  /**
   * FP-07 — the v1 colored left-border accent (Awaiting/In-Progress = blue,
   * Launched = green). `border-l-{color}` is generated after `border-border`
   * in Tailwind's utility order, so the accent always wins on the left side.
   */
  readonly accent: string;
}

/** The two scope pills (E02-F01-T08). */
const SCOPE_PILLS: readonly { value: DashboardScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine-active', label: 'My Active' },
];

export function DashboardView({ onOpen, currentUserId = null }: DashboardViewProps): JSX.Element {
  const query = useProjects();
  const clientsQuery = useClientsCache();
  const [filter, setFilter] = useState<DashboardFilter | null>(null);
  // T08 scope pill.
  const [scope, setScope] = useState<DashboardScope>('all');
  // T09 created_at range (empty string = open-ended; `<input type="date">` value).
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  // T10 Client facet ({name, id}: id set only when a cached client is picked).
  const [clientFilter, setClientFilter] = useState<{ name: string; id: number | null }>({
    name: '',
    id: null,
  });
  // T10 Position facet (typed role_name).
  const [position, setPosition] = useState('');

  const projects = useMemo(() => query.data ?? [], [query.data]);
  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const stats = useMemo(() => computeStats(projects), [projects]);
  // Position autocomplete source: distinct role titles derived from the loaded
  // portfolio (there is no distinct-roles endpoint), mapped to the ClientOption
  // shape the shared autocomplete consumes (id = stable list index).
  const positionOptions = useMemo<ClientOption[]>(
    () => distinctRoleNames(projects).map((name, index) => ({ id: index, name })),
    [projects],
  );
  const visible = useMemo(
    () =>
      applyDashboardFilters(projects, {
        filter,
        scope,
        currentUserId,
        createdFrom: createdFrom || null,
        createdTo: createdTo || null,
        clientName: clientFilter.name,
        clientId: clientFilter.id,
        position,
      }),
    [projects, filter, scope, currentUserId, createdFrom, createdTo, clientFilter, position],
  );

  const cards: StatCardDef[] = [
    {
      filter: 'kickoff',
      label: 'Awaiting Kick-off',
      count: stats.kickoff,
      accent: 'border-l-blue-500',
    },
    {
      filter: 'inprogress',
      label: 'In Progress',
      count: stats.inprogress,
      accent: 'border-l-blue-500',
    },
    {
      filter: 'launched',
      label: 'Launched',
      count: stats.launched,
      accent: 'border-l-emerald-500',
    },
  ];

  function onCardClick(f: DashboardFilter): void {
    setFilter((cur) => toggleFilter(cur, f));
  }

  return (
    <div data-testid="dashboard-view" className="space-y-6">
      {/* Header row: scope pills (All / My Active — E02-F01-T08) + New Project */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Real scope filter (E02-F01-T08): "My Active" = the current user's own
              active projects; "All" = the whole (role-scoped) portfolio. */}
          <div
            className="flex gap-2"
            data-testid="dashboard-scope-pills"
            role="group"
            aria-label="Project scope"
          >
            {SCOPE_PILLS.map((pill) => {
              const active = scope === pill.value;
              return (
                <button
                  key={pill.value}
                  type="button"
                  data-testid={`scope-${pill.value}`}
                  data-active={active ? 'true' : 'false'}
                  aria-pressed={active}
                  onClick={() => setScope(pill.value)}
                  className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition ${
                    active ? 'bg-slate-800 text-white' : 'bg-white text-gray-500 hover:bg-slate-50'
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-text-muted">
            {projects.length} project{projects.length === 1 ? '' : 's'} in your portfolio
          </p>
        </div>
        <NewProjectButton onCreated={onOpen} />
      </div>

      {/* Stat cards (also act as filter tabs) */}
      <div data-testid="dashboard-stats" className="grid gap-4 sm:grid-cols-3">
        {cards.map((card) => {
          const active = filter === card.filter;
          return (
            <button
              key={card.filter}
              type="button"
              data-testid={`stat-card-${card.filter}`}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              onClick={() => onCardClick(card.filter)}
              className={`rounded-lg border border-l-4 bg-white p-4 text-left shadow-sm transition hover:border-primary ${card.accent} ${
                active ? 'border-primary ring-2 ring-slate-400' : 'border-border'
              }`}
            >
              <span className="block text-xs font-semibold uppercase tracking-wide text-text-muted">
                {card.label}
              </span>
              <span
                data-testid={`stat-count-${card.filter}`}
                className="brand-font mt-1 block text-3xl font-bold text-text-strong"
              >
                {card.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Facet filters — Client + Position autocomplete (E02-F01-T10) and the
          created_at date range (E02-F01-T09). Replaces the single merged search
          box with dedicated, typed filters (each narrows independently, AND-combined). */}
      <div
        data-testid="dashboard-filters"
        className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-white p-3"
      >
        {/* Client facet (T10): reuses the shared ClientAutocomplete as a PURE
            filter — no "Add new" row, free text narrows, a picked client matches
            by id. */}
        <div className="w-52">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted">
            Client
          </span>
          <ClientAutocomplete
            value={clientFilter.name}
            clients={clients}
            allowAddNew={false}
            ariaLabel="Filter by client"
            onResolve={(r) => setClientFilter({ name: r.client_name, id: r.client_id })}
            placeholder="Filter by client…"
            testId="dashboard-client-filter"
          />
        </div>

        {/* Position facet (T10): distinct role titles derived client-side from the
            loaded portfolio, offered through the same autocomplete control. */}
        <div className="w-52">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted">
            Position
          </span>
          <ClientAutocomplete
            value={position}
            clients={positionOptions}
            allowAddNew={false}
            ariaLabel="Filter by position"
            onResolve={(r) => setPosition(r.client_name)}
            placeholder="Filter by position…"
            testId="dashboard-position-filter"
          />
        </div>

        {/* Created_at date range (T09). */}
        <div>
          <label
            htmlFor="dashboard-created-from"
            className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted"
          >
            Created from
          </label>
          <input
            id="dashboard-created-from"
            type="date"
            data-testid="filter-created-from"
            value={createdFrom}
            max={createdTo || undefined}
            onChange={(e) => setCreatedFrom(e.target.value)}
            className="rounded-md border border-border px-2 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <div>
          <label
            htmlFor="dashboard-created-to"
            className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted"
          >
            Created to
          </label>
          <input
            id="dashboard-created-to"
            type="date"
            data-testid="filter-created-to"
            value={createdTo}
            min={createdFrom || undefined}
            onChange={(e) => setCreatedTo(e.target.value)}
            className="rounded-md border border-border px-2 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Table / empty states */}
      {query.isPending ? (
        <div data-testid="dashboard-loading" className="p-8 text-sm text-text-muted">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
          Loading projects…
        </div>
      ) : query.isError ? (
        <div
          data-testid="dashboard-error"
          className="rounded-lg border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700"
        >
          Could not load projects.
        </div>
      ) : projects.length === 0 ? (
        <div
          data-testid="projects-list-empty"
          className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-text-muted"
        >
          No projects
        </div>
      ) : visible.length === 0 ? (
        <div
          data-testid="dashboard-no-match"
          className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-text-muted"
        >
          No projects match your search
        </div>
      ) : (
        <div
          data-testid="projects-list"
          className="overflow-x-auto rounded-lg border border-border bg-white"
        >
          <table data-testid="dashboard-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-semibold">Client / Position</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const badge = stageBadge(p.stage);
                return (
                  <tr
                    key={p.id}
                    data-testid="project-row"
                    data-project-row="true"
                    data-project-id={p.id}
                    onClick={() => onOpen(p.id)}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-muted"
                  >
                    <td className="px-4 py-3">
                      <span className="block font-semibold text-text-strong">
                        {clientLabel(p.client_name)}
                      </span>
                      <span className="block text-xs text-text-muted">
                        {roleLabel(p.role_name)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`stage-badge-${p.id}`}
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-2"
                        title={ownerLabel(p.owner_id)}
                      >
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-accent">
                          {ownerInitials(p.owner_id)}
                        </span>
                        <span className="text-text-muted">{ownerLabel(p.owner_id)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-muted">{formatUpdated(p.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {/* Legacy "Open ›" action (gold). The whole row is already
                          clickable; this button is the explicit affordance, so
                          stop propagation to avoid a double open. */}
                      <button
                        type="button"
                        data-testid={`project-open-${p.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(p.id);
                        }}
                        className="text-sm font-bold text-[#D4AF37] hover:text-[#B5952F]"
                      >
                        Open <i className="fa-solid fa-chevron-right ml-1" aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DashboardView;
